import { Table, Tag, message } from "antd";
import { useNavigate } from "react-router-dom";
import { Dispatch, useEffect, useState } from "react";
import { UseMutateAsyncFunction, useMutation, useQuery } from "react-query";
import { deleteCustomer, getCustomerById } from "../../api/customers";
import type { ColumnsType } from "antd/es/table";
import { SearchType } from "../Interfaces/SearchInterfaces";
import { useTranslation } from "react-i18next";
import { getContactTypes } from "../../api/contactType";
import { getCurrencyDropdown } from "../../api/currency";
import EditButton from "../UI/EditButton/editbutton";
import DeleteButton from "../UI/DeleteButton/deletebutton";
import TransactionButton from "../UI/TransactionButton/transactionbutton";
import DeleteModal from "../UI/ModalComponent/DeleteModal/deletemodal";
import { AxiosResponse } from "axios";
import { getCountriesDropdown } from "../../api/countries";

interface CustomersListProps {
  search: string;
  CustomerMutation: UseMutateAsyncFunction<
    AxiosResponse<any, any>,
    unknown,
    {
      limit: number;
      q: string;
    },
    unknown
  >;
  paginatedCustomers: any;
  setPaginatedCustomers: Dispatch<any>;
  isCustomerLoading: boolean;
  page: number;
  setPage: Dispatch<number>;
  tablekey: string;
}

export const CustomersList = ({
  search,
  CustomerMutation,
  isCustomerLoading,
  paginatedCustomers,
  setPaginatedCustomers,
  page,
  setPage,
  tablekey,
}: CustomersListProps) => {
  const navigate = useNavigate();
  const [t] = useTranslation("common");
  const [formatedCurrency, setFormatedCurrency] = useState<{}[]>();
  const [formatedContactType, setFormatedContactType] = useState<{}[]>();
  const [prevPage, setPrevPage] = useState<number>(0);
  const [maxPage, setMaxPage] = useState<number>(0);
  const [open, setOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState<string | undefined>(undefined);
  const [nameToDelete, setNameToDelete] = useState<any>();
  const [, setError] = useState("");

  useEffect(() => {
    page > prevPage &&
      maxPage < page &&
      CustomerMutation({ limit: page * 10 + 10, q: search }).then((res) => {
        if (page > maxPage) setMaxPage(page);

        setPaginatedCustomers(res.data.data);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const { isLoading: loadingContactType } = useQuery(
    "contactTypes",
    getContactTypes,
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setFormatedContactType(
          res.data.map((contactType: any) => {
            return { value: contactType.id, label: contactType.typeName };
          })
        );
      },
    }
  );
  const { isLoading: loadingCurrency } = useQuery(
    "currencies",
    getCurrencyDropdown,
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setFormatedCurrency(
          res.data.map((curr: any) => {
            return { value: curr.id, label: curr.symbol };
          })
        );
      },
    }
  );

  const [formatedCountries, setFormatedCountries] = useState<{}[]>();
  const { isLoading: loadingCountries } = useQuery(
    "countries",
    getCountriesDropdown,
    {
      refetchOnWindowFocus: false,
      onSuccess: (res) => {
        setFormatedCountries(
          res.data?.map((country: any) => {
            return { value: country.id, label: country.name };
          })
        );
      },
    }
  );

  const getCustomerMutation = useMutation("getCustomer", getCustomerById);
  function fillCustomerById(id: any) {
    getCustomerMutation.mutateAsync(id).then((res) => {
      navigate(`/customers/${id}`, {
        state: {
          customer: res.data,
          formatedCurrency: formatedCurrency,
          loadingCurrency: loadingCurrency,
          formatedContactType: formatedContactType,
          loadingCopntactType: loadingContactType,
          formatedCountries: formatedCountries,
          loadingCountries: loadingCountries,
        },
      });
    });
  }
  const onError = (error: any, context: unknown) => {
    if (setError) {
      setError(error.response.data.message);
      message.error({
        content: error.response.data.message,
        duration: 10,
        onClick: () => {
          message.destroy();
        },
      });
    }
  };
  const deleteCustomerMutation = useMutation("deleteCustomer", deleteCustomer, {
    onSuccess: () => {
      CustomerMutation({ limit: page * 10 + 10, q: search }).then((res) => {
        setPaginatedCustomers(res.data?.data);
        message.success("Complete successfully");
      });
    },
    onError,
  });

  const handleDelete = (id: string | undefined, name: string): void => {
    setOpen(true);
    setIdToDelete(id);
    setNameToDelete(name);
  };

  const handleOk = () => {
    if (idToDelete) {
      deleteCustomerMutation.mutate({ id: idToDelete });
    }
    setOpen(false);
  };

  const columns: ColumnsType<SearchType> = [
    {
      title: `${t("name")}`,
      dataIndex: "name",
      key: "name",
      className: "table-header",
    },
    {
      title: `${t("code")}`,
      dataIndex: "code",
      key: "code",
      width: "8rem",
    },
    {
      title: `${t("phone-number")}`,
      dataIndex: "phone",
      key: "phone",
      width: "10rem",
      render: (phone) => {
        if (phone) {
          const phoneNumberParts = phone.split("/");
          const phoneNumber =
            phoneNumberParts.length > 1 ? phoneNumberParts[1] : phone;
          return <span>{phoneNumber}</span>;
        }
        return null;
      },
    },
    {
      title: `${t("email")}`,
      dataIndex: "email",
      key: "email",
      width: "18rem",
    },
    {
      title: `${t("notes")}`,
      dataIndex: "notes",
      key: "notes",
      width: "18rem",
    },
    {
      title: `${t("status")}`,
      align: "center",
      dataIndex: "isActive",
      width: "5rem",
      key: "isActive",
      render: (record: any) =>
        record ? (
          <Tag className="active">Active</Tag>
        ) : (
          <Tag className="inactive">Inactive</Tag>
        ),
    },
    {
      title: `${t("action")}`,
      dataIndex: "action",
      key: "action",
      width: "7rem",
      fixed: "right",
      align: "center",
      render: (index, record) => (
        <>
          <TransactionButton
            onClick={() =>
              navigate(`/customerbalance`, {
                state: { id: record.id, name: record.name },
              })
            }
          />
          <EditButton onClick={() => fillCustomerById(record.id)} />
          <DeleteButton
            onClick={() => handleDelete(record?.id, record?.name)}
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <DeleteModal
        open={open}
        setOpen={setOpen}
        name={nameToDelete}
        onOk={handleOk}
      />
      <Table
        key={tablekey}
        scroll={{ x: 1000 }}
        columns={columns}
        dataSource={paginatedCustomers}
        loading={isCustomerLoading}
        rowClassName={(record, index) =>
          index % 2 === 0
            ? "table-row-light table-row"
            : "table-row-dark table-row"
        }
        rowKey={(record) => record.id}
        onChange={(selectedPage) => {
          setPrevPage(page);
          setPage(selectedPage?.current ?? 1);
        }}
      />
    </div>
  );
};
